import type {
  ExtendedPermissionSet,
  MemberPermissionsResponse,
} from '@/lib/api/permissions.api'
import { permissionsApi } from '@/lib/api/permissions.api'
import { useQuery } from '@tanstack/react-query'

import type { ModuleTypeEnum, PermissionDefinition } from '@scrambl/shared'

import { queryKeys } from '../query-keys'
import { usePermissionQueryScope } from './permission-query-scope'

/**
 * Hook to fetch permission definitions, optionally filtered by module type
 */
export function usePermissionDefinitionsQuery(
  moduleType?: ModuleTypeEnum,
  options?: { enabled?: boolean; forceRefresh?: boolean },
) {
  const scope = usePermissionQueryScope()

  return useQuery<PermissionDefinition[]>({
    queryKey: [
      ...queryKeys.permissions.definitions.list(moduleType),
      ...scope.key,
    ],
    queryFn: ({ signal }) =>
      scope.run(signal, signal =>
        permissionsApi.getDefinitions(
          moduleType,
          options?.forceRefresh || false,
          { signal },
        ),
      ),
    enabled: options?.enabled !== false && scope.enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook to fetch all permission sets
 */
export function usePermissionSetsQuery(options?: { enabled?: boolean }) {
  const scope = usePermissionQueryScope()

  return useQuery<ExtendedPermissionSet[]>({
    queryKey: [...queryKeys.permissions.sets.list(), ...scope.key],
    queryFn: ({ signal }) =>
      scope.run(signal, signal =>
        permissionsApi.getPermissionSets(undefined, false, { signal }),
      ),
    enabled: options?.enabled !== false && scope.enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook to fetch permissions for a specific member
 */
export function useMemberPermissionsQuery(
  memberId: string | undefined,
  options?: { enabled?: boolean },
) {
  const scope = usePermissionQueryScope()

  return useQuery<MemberPermissionsResponse>({
    queryKey: [
      ...queryKeys.permissions.memberPermissions(memberId!),
      ...scope.key,
    ],
    queryFn: ({ signal }) =>
      scope.run(signal, signal => {
        if (!memberId) throw new Error('Member ID is required')
        return permissionsApi.getMemberPermissionDetails(memberId, { signal })
      }),
    enabled: options?.enabled !== false && !!memberId && scope.enabled,
  })
}
